import {
  ProjectManagementProjectDetailsPageSettingsBudgetSectionCreateBudgetStrategyDocument,
  ProjectManagementProjectDetailsPageSettingsBudgetSectionCreateBudgetStrategyMutation,
  ProjectManagementProjectDetailsPageSettingsBudgetSectionDeleteBudgetStrategyDocument,
  ProjectManagementProjectDetailsPageSettingsBudgetSectionDeleteBudgetStrategyMutation,
  ProjectManagementProjectDetailsPageSettingsBudgetSectionUpdateBudgetStrategyDocument,
  ProjectManagementProjectDetailsPageSettingsBudgetSectionUpdateBudgetStrategyMutation,
  ProjectManagementProjectDetailsPageSettingsPageDocument,
  ProjectManagementValueObjectsBudgetStrategyTypeEnum,
} from 'generated/resources/graphql'

import { useMutation } from 'modules/core/hooks/useMutation'

interface createBudgetStrategyDto {
  budgetStrategyType: ProjectManagementValueObjectsBudgetStrategyTypeEnum
  budgetAllocation?: number
  fixedCostCents?: number
  feeAmountCents?: number
  isBillable?: boolean
  projectId: number
  plannedCents?: number
  plannedMinutes?: number
  delegated: boolean
}
interface updateBudgetStrategyDto {
  id: number
  budgetAllocation?: number
  budgetStrategyType: ProjectManagementValueObjectsBudgetStrategyTypeEnum
  feeAmountCents?: number
  fixedCostCents?: number
  isBillable?: boolean
  plannedCents?: number
  plannedMinutes?: number
}

export const useCreateBudgetStrategy = (companyId: number) => {
  const [createBudgetStrategy] = useMutation(
    ProjectManagementProjectDetailsPageSettingsBudgetSectionCreateBudgetStrategyDocument,
    {
      extractorFn: (
        data: ProjectManagementProjectDetailsPageSettingsBudgetSectionCreateBudgetStrategyMutation
      ) => data.projectManagementMutations.createProjectmanagementBudgetStrategy,
    }
  )

  return {
    createBudgetStrategy(dto: createBudgetStrategyDto) {
      const {
        budgetStrategyType,
        budgetAllocation,
        feeAmountCents,
        fixedCostCents,
        isBillable,
        plannedCents,
        plannedMinutes,
        projectId,
        delegated,
      } = dto
      return createBudgetStrategy({
        variables: {
          budgetStrategyType,
          budgetAllocation,
          feeAmountCents,
          fixedCostCents,
          isBillable,
          plannedCents,
          plannedMinutes,
          projectId,
          delegated,
        },
        awaitRefetchQueries: true,
        refetchQueries: [
          {
            query: ProjectManagementProjectDetailsPageSettingsPageDocument,
            variables: {
              companyId,
              projectId,
              projectBudgetIsEnabled: true,
              projectCostsIsEnabled: true,
            },
          },
        ],
      })
    },
  }
}

export const useUpdateBudgetStrategy = () => {
  const [updateBudgetStrategy] = useMutation(
    ProjectManagementProjectDetailsPageSettingsBudgetSectionUpdateBudgetStrategyDocument,
    {
      extractorFn: (
        data: ProjectManagementProjectDetailsPageSettingsBudgetSectionUpdateBudgetStrategyMutation
      ) => data.projectManagementMutations.updateProjectmanagementBudgetStrategy,
    }
  )

  return {
    updateBudgetStrategy(dto: updateBudgetStrategyDto) {
      const {
        id,
        budgetAllocation,
        budgetStrategyType,
        feeAmountCents,
        fixedCostCents,
        isBillable,
        plannedCents,
        plannedMinutes,
      } = dto
      return updateBudgetStrategy({
        variables: {
          id,
          budgetAllocation,
          budgetStrategyType,
          feeAmountCents,
          fixedCostCents,
          isBillable,
          plannedCents,
          plannedMinutes,
        },
        optimisticResponse: {
          projectManagementMutations: {
            updateProjectmanagementBudgetStrategy: {
              errors: [],
              budgetStrategy: {
                __typename: 'ProjectManagementBudgetStrategy',
                id,
                budgetAllocation,
                budgetType: budgetStrategyType,
                feeAmountCents: feeAmountCents ?? null,
                plannedCents: plannedCents ?? null,
                plannedMinutes: plannedMinutes ?? null,
              },
            },
          },
        },
      })
    },
  }
}

export const useDeleteBudgetStrategy = () => {
  const [deleteBudgetStrategy] = useMutation(
    ProjectManagementProjectDetailsPageSettingsBudgetSectionDeleteBudgetStrategyDocument,
    {
      extractorFn: (
        data: ProjectManagementProjectDetailsPageSettingsBudgetSectionDeleteBudgetStrategyMutation
      ) => data.projectManagementMutations.deleteProjectmanagementBudgetStrategy,
    }
  )

  return {
    deleteBudgetStrategy({ id }: { id: number }) {
      return deleteBudgetStrategy({
        variables: { id },
        update(cache, mutationResult) {
          const deletedBudgetStrategy =
            mutationResult.data?.projectManagementMutations.deleteProjectmanagementBudgetStrategy
          if (deletedBudgetStrategy) {
            cache.evict({
              id: cache.identify({
                id: id,
                __typename: 'ProjectManagementBudgetStrategy',
              }),
            })
          }
        },
      })
    },
  }
}
